import {
  Badge,
  Collapse,
  Group,
  HoverCard,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useState } from 'react';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import { TOfferActivity } from '@/models/offers';

const CUSTOMER_ACTION_LABELS: Record<number, string> = {
  10: 'Viewed offer',
  11: 'Opened cart',
  12: 'Submitted order',
  13: 'Payment confirmed by client',
  14: 'Order processing failed',
  20: 'KYC started',
  21: 'KYC link generated',
  22: 'KYC delegate sent',
  23: 'KYC approved',
  24: 'KYC rejected',
  25: 'KYC cancelled',
  30: 'Invoice viewed',
  31: 'Invoice downloaded',
  40: 'Paying party change',
  51: 'Payer declined',
  52: 'Payer accepted',
};

const USER_ACTION_LABELS: Record<number, string> = {
  20: 'Discount set to',
  21: 'Expiry date set to',
  22: 'Added',
  23: 'Removed',
  24: 'Offer sent',
  25: 'Offer unlocked',
  26: 'Payer attached',
  27: 'Payer detached',
  28: 'Invoice paid',
  29: 'Payment confirmation submitted',
  33: 'Offer archived',
  34: 'Offer unarchived',
  35: 'Split mode cancelled - back to single invoice',
  38: 'New proforma issued',
};

const EMAIL_TEMPLATE_LABELS: Record<string, string> = {
  offer: 'Offer emailed',
  offer_resend: 'Offer re-sent',
  invoice: 'Invoice emailed',
  renewal_reminder: 'Renewal reminder emailed',
  kyc_delegate: 'Verification link emailed',
  portal_invite: 'Portal invitation emailed',
  offer_no_proforma: 'No-active-proforma notice emailed',
};

const EMAIL_STATUS_LABELS: Record<string, string> = {
  queued: 'queued',
  submission_unknown: 'delivery unconfirmed',
  accepted: 'sent',
  delivered: 'delivered',
  opened: 'opened',
  bounced: 'bounced',
  complained: 'marked as spam',
  unsubscribed: 'unsubscribed',
  failed: 'failed',
};

export function formatEmailActivity(a: TOfferActivity): string {
  const meta = a.meta as Record<string, unknown>;
  const template = meta.template as string | undefined;
  const kind = meta.recipientKind as string | undefined;
  const to = meta.to as string | undefined;
  const status = meta.status as string | undefined;
  const head = EMAIL_TEMPLATE_LABELS[template ?? ''] ?? 'Email sent';
  const who = kind && kind !== 'contact' ? `${kind} ` : '';
  const state = EMAIL_STATUS_LABELS[status ?? ''] ?? status;
  return `${head} to ${who}${to ?? 'unknown recipient'}${
    state ? ` - ${state}` : ''
  }`;
}

export function formatActivity(a: TOfferActivity): string {
  if (a.source === 'email') return formatEmailActivity(a);
  const labels =
    a.source === 'user' ? USER_ACTION_LABELS : CUSTOMER_ACTION_LABELS;
  const label = labels[a.action] || `Action ${a.action}`;
  const meta = a.meta as Record<string, unknown> | undefined;
  const value = meta?.value as string | null | undefined;
  const name = meta?.name as string | undefined;
  const by = meta?.by as string | undefined;

  const parts = [label];

  if (a.source === 'user' && (a.action === 20 || a.action === 21)) {
    parts.push(value ?? 'none');
  } else if (
    a.source === 'user' &&
    (a.action === 28 || a.action === 29 || a.action === 38)
  ) {
    const invoiceNumber = meta?.invoiceNumber as string | undefined;
    if (invoiceNumber) parts.push(`#${invoiceNumber}`);
  } else if (a.source === 'customer' && a.action === 14) {
    const error = meta?.error as string | undefined;
    if (error) parts.push(`- ${error.split('\n')[0].slice(0, 80)}`);
  } else if (a.source === 'user' && (a.action === 26 || a.action === 27)) {
    const payerName = meta?.payerDisplayName as string | undefined;
    if (payerName) parts.push(`"${payerName}"`);
  } else if (a.source === 'customer' && a.action === 24) {
    const reason = meta?.reason as string | undefined;
    const status = meta?.status as string | undefined;
    if (status === 'retry') parts[0] = 'KYC rejected (retry)';
    if (reason) parts.push(`- ${reason}`);
  } else if (name) {
    parts.push(`"${name}"`);
  }

  if (by) parts.push(`by ${by}`);
  return parts.join(' ');
}

function isError(a: TOfferActivity): boolean {
  return a.source === 'customer' && a.action === 14;
}

function isWarning(a: TOfferActivity): boolean {
  return a.source === 'customer' && a.action === 24;
}

const fmtDate = (t: string) => dayjs(t).format('DD.MM.YYYY');
const fmtTime = (t: string) => dayjs(t).format('HH:mm:ss');
const fmtFull = (t: string) => dayjs(t).format('HH:mm:ss  DD.MM.YYYY');

type AggregatedEntry = {
  label: string;
  latest: TOfferActivity;
  all: TOfferActivity[];
};

function aggregateActivity(activity: TOfferActivity[]): AggregatedEntry[] {
  const result: AggregatedEntry[] = [];
  for (const a of activity) {
    const label = formatActivity(a);
    const prev = result[result.length - 1];
    if (prev && prev.label === label) {
      prev.all.push(a);
      if (new Date(a.time) > new Date(prev.latest.time)) {
        prev.latest = a;
      }
    } else {
      result.push({ label, latest: a, all: [a] });
    }
  }
  return result;
}

type DateGroup = {
  date: string;
  entries: AggregatedEntry[];
};

function groupByDate(entries: AggregatedEntry[]): DateGroup[] {
  const result: DateGroup[] = [];
  for (const entry of entries) {
    const date = fmtDate(entry.latest.time);
    const prev = result[result.length - 1];
    if (prev && prev.date === date) {
      prev.entries.push(entry);
    } else {
      result.push({ date, entries: [entry] });
    }
  }
  return result;
}

function ActivityEntry({ entry }: { entry: AggregatedEntry }) {
  const color = isError(entry.latest)
    ? 'red'
    : isWarning(entry.latest)
      ? 'orange'
      : undefined;
  const count = entry.all.length;

  const content = (
    <Text component="span" fz="sm" c={color}>
      <Text span fz="xs" c="dimmed">
        {fmtTime(entry.latest.time)}
      </Text>{' '}
      {entry.label}
      {count > 1 && (
        <>
          {' '}
          <Badge
            size="xs"
            variant="light"
            circle
            style={{ display: 'inline-flex' }}
          >
            {count}
          </Badge>
        </>
      )}
    </Text>
  );

  if (count <= 1) return content;

  return (
    <HoverCard width={280} shadow="md" openDelay={200}>
      <HoverCard.Target>
        <div style={{ cursor: 'default' }}>{content}</div>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={2}>
          {entry.all.map((a, i) => (
            <Text key={i} fz="sm" c={color} style={{ whiteSpace: 'nowrap' }}>
              <Text span fz="xs" c="dimmed">
                {fmtFull(a.time)}
              </Text>{' '}
              {entry.label}
            </Text>
          ))}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

function DateGroupSection({
  group,
  defaultOpen,
}: {
  group: DateGroup;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = open ? PiCaretDown : PiCaretRight;

  return (
    <Stack gap={4}>
      <UnstyledButton onClick={() => setOpen((o) => !o)}>
        <Group gap={4}>
          <Icon size={12} color="var(--mantine-color-dimmed)" />
          <Text fz="xs" fw={500} c="dimmed">
            {group.date}
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse expanded={open}>
        <Stack gap={4}>
          {group.entries.map((entry, i) => (
            <ActivityEntry key={i} entry={entry} />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export default function OfferActivityTimeline({
  activity,
}: {
  activity: TOfferActivity[];
}) {
  if (activity.length === 0) {
    return (
      <Text fz="xs" c="dimmed">
        No activity yet.
      </Text>
    );
  }

  const entries = aggregateActivity(activity);
  const dateGroups = groupByDate(entries);

  return (
    <Stack gap="sm">
      {dateGroups.map((group, i) => (
        <DateGroupSection
          key={group.date}
          group={group}
          defaultOpen={i === 0}
        />
      ))}
    </Stack>
  );
}
